import { useState, useEffect } from 'react';
import { useAPIQuery } from './useAPIQuery';
import { useSnapshot } from 'valtio';
import { sessionState } from '@/app/sessionState';
import { relativeDateReviver } from '@/utils';
import config from '@/app/config';

/**
 * useAPI Hook
 * 
 * @param {string|object} request - API endpoint URL or request configuration object
 * @param {object} options - Optional React Query options
 * @returns {object} { data, response } - data: the actual API data, response: { isLoading, isError, error, refetch }
 */
export function useAPIRequest(request, options) {
  const { demoData } = useSnapshot(sessionState);

  // Process the request to handle sample data switching
  const processedRequest = processRequest(request);
  //console.log('Processed API Request:', request, processedRequest);
  const { data, isLoading, isError, error, refetch } = useAPIQuery(processedRequest, options);

  // Handle demo data override
  let finalData = data;
  if (shouldUseDemoData(processedRequest, demoData)) {    
    finalData = parseDemoData(demoData.data) || data;
  }

  // Handle API error codes
  if (finalData?.ErrorCode) {
    return {
      data: null,
      response: {
        isLoading: false,
        isError: true,
        error: { code: finalData.ErrorCode, data: finalData },
        ok: false,
        refetch
      }
    };
  }

  return {
    data: finalData,
    response: {
      isLoading,
      isError,
      error,
      ok: !isLoading && !isError,
      refetch
    }
  };
}

function processRequest(request) {
  let processedRequest = request;

  // Handle string URLs
  if (typeof request === 'string') {
    const isSampleData = request.indexOf("adenin-catalog.netlify.app") > -1;
    if (isSampleData && sessionState.cardId) {
      processedRequest = `${config.API_URL}/api/gateway/card/${sessionState.cardId}?channel=artifacts&includeCardDefinition=true`;
    }
    return processedRequest;
  }

  // Handle request objects
  if (typeof request === 'object' && request.url) {
    const isSampleData = request.url.indexOf("adenin-catalog.netlify.app") > -1;
    if (isSampleData && sessionState.cardId) {
      processedRequest = {
        ...request,
        url: `${config.API_URL}/api/gateway/card/${sessionState.cardId}?channel=artifacts&includeCardDefinition=true`
      };
    }

    // Switch to sampleData when url parameter is empty
    if (request.parameters?.url !== undefined && !request.parameters.url && request.sampleData) {
      processedRequest = {
        ...request,
        url: request.sampleData
      };
    }
  }

  return processedRequest;
}

function shouldUseDemoData(request, demoData) {
  if (!demoData?.data || demoData.data === '{}' || !demoData.enabled) {
    return false;
  }

  // Use demo data if request matches sampleData or has no sampleData
  const requestUrl = typeof request === 'string' ? request : request?.url;
  const sampleDataUrl = typeof request === 'object' ? request?.sampleData : null;

  return requestUrl === sampleDataUrl || !sampleDataUrl;
}

function parseDemoData(demoDataString) {
  try {
    let parsed = JSON.parse(demoDataString, relativeDateReviver);
    if (Array.isArray(parsed)) {
      parsed = { items: parsed };
    }
    return parsed;
  } catch (e) {
    console.error('Failed to parse demo data:', e);
    return null;
  }
}